import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { type Ad, CAMPAIGN_STATUSES } from '@q4udoc/types';
import {
  Button,
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  EditButton,
  List,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useGetList,
  useRecordContext,
} from 'react-admin';
import { Link } from 'react-router-dom';
import AdPreviewPanel from '../components/AdPreviewPanel';
import { CentsField } from '../components/CentsField';

// Every CampaignStatus, 'archived' included, so archived campaigns can be filtered and show a
// populated status control instead of a blank one. The label is the capitalised id.
const campaignStatusChoices = CAMPAIGN_STATUSES.map((id) => ({
  id,
  name: id.charAt(0).toUpperCase() + id.slice(1),
}));

export const CampaignList = () => (
  <List
    filters={[
      <ReferenceInput key="advertiserId" source="advertiserId" reference="advertiser-accounts">
        <SelectInput optionText="name" label="Advertiser" />
      </ReferenceInput>,
      <SelectInput key="status" source="status" choices={campaignStatusChoices} />,
    ]}
    empty={
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom>
          No Campaigns Found
        </Typography>
        <Button
          component={Link}
          to="/campaigns/create"
          variant="contained"
          label="Create Campaign"
          startIcon={<AddIcon />}
        />
      </Box>
    }
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <ReferenceField source="advertiserId" reference="advertiser-accounts">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="name" />
      <TextField source="status" />
      <DateField source="startDate" />
      <DateField source="endDate" />
    </Datagrid>
  </List>
);

export const CampaignEdit = () => (
  <Edit>
    <SimpleForm>
      {/* advertiserId is read-only on edit: it's the billing/attribution key and
                is pinned create-only server-side (the route strips it from updates), so
                a campaign can't be reassigned to another advertiser. */}
      <ReferenceInput source="advertiserId" reference="advertiser-accounts">
        <SelectInput
          optionText="name"
          disabled
          helperText="Set at creation — a campaign can't be reassigned to another advertiser."
        />
      </ReferenceInput>
      <TextInput source="name" />
      <DateInput source="startDate" />
      <DateInput source="endDate" />
      <SelectInput source="status" choices={campaignStatusChoices} />
      <NumberInput source="budget" />
    </SimpleForm>
  </Edit>
);

export const CampaignCreate = () => (
  <Create>
    <SimpleForm>
      <ReferenceInput source="advertiserId" reference="advertiser-accounts">
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="name" />
      <DateInput source="startDate" />
      <DateInput source="endDate" />
      <SelectInput source="status" defaultValue="draft" choices={campaignStatusChoices} />
      <NumberInput source="budget" />
    </SimpleForm>
  </Create>
);

const CreateAdButton = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <Button
      component={Link}
      to={{
        pathname: '/ads/create',
        search: `?source=${JSON.stringify({ campaignId: record.id })}`,
      }}
      label="Create Ad"
      startIcon={<AddIcon />}
    />
  );
};

const CampaignShowActions = () => (
  <TopToolbar>
    <EditButton />
    <CreateAdButton />
  </TopToolbar>
);

const AdPreviewGrid = () => {
  const record = useRecordContext();
  const { data: ads = [], isLoading } = useGetList<Ad>('ads', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'priority', order: 'DESC' },
    filter: { campaignId: record?.id },
  });

  if (isLoading)
    return (
      <Typography variant="body2" sx={{ py: 2, color: '#9ca3af' }}>
        Loading ads...
      </Typography>
    );
  if (ads.length === 0)
    return (
      <Typography variant="body2" sx={{ py: 2, color: '#9ca3af' }}>
        No ads in this campaign.
      </Typography>
    );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
      {ads.map((ad) => (
        <Box key={ad.id}>
          <AdPreviewPanel data={ad} />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, mb: 1 }}>
            <Chip label={ad.placement} size="small" variant="outlined" />
            <Chip label={`Priority: ${ad.priority}`} size="small" variant="outlined" />
            {ad.enabled ? (
              <Chip label="Enabled" size="small" color="success" variant="outlined" />
            ) : (
              <Chip label="Disabled" size="small" color="default" variant="outlined" />
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const CampaignShow = () => (
  <Show actions={<CampaignShowActions />}>
    <SimpleShowLayout>
      <ReferenceField source="advertiserId" reference="advertiser-accounts">
        <TextField source="name" />
      </ReferenceField>
      <TextField source="name" />
      <TextField source="status" />
      <DateField source="startDate" />
      <DateField source="endDate" />
      <NumberField source="budget" options={{ style: 'currency', currency: 'USD' }} />
      <CentsField source="spentCents" label="Ad Spend" />

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Ad Previews
      </Typography>
      <AdPreviewGrid />

      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        All Ads
      </Typography>
      <ReferenceManyField reference="ads" target="campaignId">
        <Datagrid rowClick="edit">
          <TextField source="title" />
          <TextField source="placement" />
          <TextField source="priority" />
        </Datagrid>
      </ReferenceManyField>
    </SimpleShowLayout>
  </Show>
);
